import { cardLabel, type PublicRoom, type VoteSummary } from '@planning-session/shared';
import styles from './Results.module.css';

export interface ResultsProps {
  readonly room: PublicRoom;
  readonly summary: VoteSummary;
}

function spread(summary: VoteSummary): string {
  if (summary.min === null || summary.max === null) return '—';
  return summary.min === summary.max ? cardLabel(summary.min) : `${cardLabel(summary.min)} – ${cardLabel(summary.max)}`;
}

/**
 * The numbers behind the verdict. The verdict itself lives on the table, where
 * the reveal happens — this panel never repeats it.
 */
export function Results({ room, summary }: ResultsProps) {
  const votes = Math.max(summary.total, 1);
  const nonNumeric = summary.total - summary.numericCount;

  return (
    <section className={styles.panel} aria-label="Round detail">
      <div className={styles.stats}>
        <span className={styles.stat}>
          <span className={styles.statLabel}>Average</span>
          <span className={styles.statValue}>{summary.average ?? '—'}</span>
        </span>
        <span className={styles.stat}>
          <span className={styles.statLabel}>Median</span>
          <span className={styles.statValue}>{summary.median ?? '—'}</span>
        </span>
        <span className={styles.stat}>
          <span className={styles.statLabel}>Spread</span>
          <span className={styles.statValue}>{spread(summary)}</span>
        </span>
        <span className={styles.stat}>
          <span className={styles.statLabel}>Voted</span>
          <span className={styles.statValue}>
            {summary.total} / {room.playerCount}
          </span>
        </span>
      </div>

      <p className={styles.note}>
        {summary.consensus
          ? 'Write it down and move on.'
          : 'Let the highest and lowest card explain their thinking before voting again.'}
        {nonNumeric > 0 &&
          ` ${nonNumeric} non-numeric ${nonNumeric === 1 ? 'card was' : 'cards were'} left out of the average.`}
      </p>

      <div className={styles.distribution}>
        <span className={styles.distributionLabel}>How the room voted</span>
        <div className={styles.rows}>
          {summary.distribution.map((tally) => (
            <div key={tally.value} className={styles.row}>
              <span className={styles.rowCard}>{cardLabel(tally.value)}</span>
              <span className={styles.track}>
                <span className={styles.fill} style={{ transform: `scaleX(${tally.count / votes})` }} />
              </span>
              <span className={styles.rowCount}>
                {tally.count} {tally.count === 1 ? 'vote' : 'votes'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
